'use client'

import { DIALOG_MOBILE_FULL } from '@trustoo/components/modal/constants/dialogPresets'
import Dialog from '@trustoo/components/modal/Dialog'
import { clsx } from 'clsx'
import { ReactNode } from 'react'

export interface ModalProps {
    /** Whether the modal is open. */
    open: boolean
    /**
     * Requested when the user closes the modal (close button, backdrop click, Escape).
     * Flip `open` to `false` in response; the modal then animates out and closes itself.
     */
    onClose: () => void
    children: ReactNode
    /** Max width of the modal card. A number is treated as pixels. Defaults to 500px (legacy parity). */
    maxWidth?: number | string
    /** Wrap the children in the default 24px padding. Set `false` for edge-to-edge content. */
    wrapContent?: boolean
    /** Close when the backdrop (the area outside the card) is clicked. Off by default. */
    closeOnOutsideClick?: boolean
    /** Hide the close cross entirely (e.g. for flows that must be completed). */
    hideCloseButton?: boolean
    /** Custom close-button content. Defaults to the grey close icon. */
    closeButton?: ReactNode
    /** Extra classes for the close button. */
    closeButtonClassName?: string
    /**
     * Accessible name for the (icon-only) close button, set as its `aria-label`. Pass a localised
     * string. Omit only when `closeButton` already provides its own visible/accessible text.
     */
    closeButtonLabel?: string
    /** Full-screen, square-cornered on mobile (below the `md` breakpoint). */
    mobileFull?: boolean
    /** Extra classes for the modal card. */
    className?: string
    /** Fired once the exit animation has finished and the modal has closed. */
    afterExit?: () => void
}

/**
 * Modal — drop-in replacement for the legacy `@advanza/modal` `Modal`.
 *
 * A higher-level, opinionated wrapper over the shared `Dialog` building block. It keeps the
 * legacy Modal's look (white card, dark fading backdrop, scale + fade transition, 24px content
 * padding, grey close icon) and a comparable prop surface (`open` / `onClose` / `maxWidth` /
 * `wrapContent` / `closeButton` / `closeOnOutsideClick` / `afterExit`), but is built on the native
 * `<dialog>` element instead of `CSSTransition` + a manual portal.
 *
 * Always animated (KISS) — it renders `Dialog.AnimatedRoot`. For a static dialog, or low-level
 * control (custom presets, no content wrapper), compose `Dialog.Root` / `Dialog.AnimatedRoot` /
 * `Dialog.CloseButton` directly instead.
 *
 * Controlled via `open` + `onClose`: the close button, backdrop click and Escape all call `onClose`;
 * flip `open` in response.
 *
 * @example
 * ```tsx
 * const { isOpen, open, close } = useDialog()
 *
 * <button onClick={open}>Open</button>
 * <Modal open={isOpen} onClose={close} maxWidth={600} closeOnOutsideClick>
 *   <p>Modal content</p>
 * </Modal>
 * ```
 */
const Modal = ({
    children,
    open,
    onClose,
    maxWidth = 500,
    wrapContent = true,
    closeOnOutsideClick = false,
    hideCloseButton = false,
    closeButton,
    closeButtonClassName,
    closeButtonLabel,
    mobileFull = false,
    className,
    afterExit,
}: ModalProps) => (
    <Dialog.AnimatedRoot
        open={open}
        onClose={onClose}
        onExited={afterExit}
        closeOnBackdropClick={closeOnOutsideClick}
        style={{ maxWidth }}
        className={clsx(
            // Legacy Modal card look: 5px corners, layered soft shadow, min height.
            'min-h-[130px] w-[calc(100vw-24px)] overflow-hidden rounded-[5px] bg-white shadow-[0_0_80px_0_#00000033,0_0_25px_0_#00000026]',
            mobileFull && DIALOG_MOBILE_FULL,
            className,
        )}>
        {!hideCloseButton && (
            <Dialog.CloseButton
                onClick={onClose}
                className={closeButtonClassName}
                aria-label={closeButtonLabel}>
                {closeButton}
            </Dialog.CloseButton>
        )}
        {wrapContent ? <div className="p-6">{children}</div> : children}
    </Dialog.AnimatedRoot>
)

export default Modal
